import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core'
import { Subscription } from 'rxjs'

import { ClientLeaseDocuments } from '@data/interfaces/documents.interface'
import { ApiLeaseService } from '../../data/services/api/api-lease.service'
import { AuthService } from '@data/services/modules/auth/auth.service'

/**
 * F-009 · Documentos del detalle del cliente: la pestaña «Expediente» (el oficial, editable y con revisión) y una
 * pestaña de solo lectura por arrendamiento con la copia que vive en él. Las de arrendamiento exigen
 * `leases.get-all` (Contabilidad no las ve) y no se refrescan al editar el expediente: la copia se hace a mano
 * desde el arrendamiento.
 */
@Component({
  selector: 'app-customer-documents-tabs',
  templateUrl: './customer-documents-tabs.component.html',
  styleUrls: ['./customer-documents-tabs.component.scss'],
})
export class CustomerDocumentsTabsComponent implements OnChanges, OnDestroy {
  /** El cliente con `docs` poblado, `rfc` y `documentType`, tal como lo carga `customer-detail`. */
  @Input() client: any
  /** Se reemite el `changed` del expediente para que `customer-detail` recargue al cliente. */
  @Output() changed = new EventEmitter<void>()

  readonly canViewLeases: boolean
  leases: ClientLeaseDocuments[] = []
  leasesLoading = false
  leasesError = false

  private loadedClientId = ''
  private subscription?: Subscription

  constructor(
    private apiLease: ApiLeaseService,
    auth: AuthService,
  ) {
    this.canViewLeases = auth.userHasAuthorization('leases', 'get-all')
  }

  //* El cliente se recarga entero cada vez que el expediente cambia; los arrendamientos solo se piden de nuevo si
  //* cambia el cliente, no en cada recarga ni en cada cambio de pestaña.
  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['client'] || !this.canViewLeases) return
    const clientId = this.client?._id ? String(this.client._id) : ''
    if (clientId && clientId !== this.loadedClientId) this.loadLeases(clientId)
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe()
  }

  loadLeases(clientId = this.loadedClientId): void {
    this.loadedClientId = clientId
    this.subscription?.unsubscribe()
    this.leases = []
    this.leasesLoading = true
    this.leasesError = false
    this.subscription = this.apiLease.getByClientWithDocuments(clientId).subscribe({
      next: (leases) => {
        //* Sin populate (`documents` en crudo o ausente) no hay copia que mostrar: se trata como «sin copia».
        this.leases = leases.map((lease) => ({ ...lease, documents: lease.documents && typeof lease.documents === 'object' ? lease.documents : null }))
        this.leasesLoading = false
      },
      error: () => {
        this.leasesError = true
        this.leasesLoading = false
      },
    })
  }

  leaseLabel(lease: ClientLeaseDocuments, index: number): string {
    return `${lease.folio || `Arrendamiento ${index + 1}`} · ${lease.status || 'Sin estado'}`
  }

  trackById(_index: number, lease: ClientLeaseDocuments): string {
    return lease._id
  }
}
